// 3rd party
import * as React from 'react';
import { observer } from 'mobx-react';

// Shared UI
import { useLocalization, LocalizedText } from '@shared-ui/localization-context';

// UI Toolkit
import { UitkPrimaryButton, UitkSecondaryButton } from 'uitk-react-button';
import { UitkInformativeInput, UitkNumberInput } from 'uitk-react-input';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkSelect } from 'uitk-react-select';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkRadio, UitkRadioGroup } from 'uitk-react-switch';
import { UitkHeading, UitkText } from 'uitk-react-text';

// Components
import { MoreInformationDialog, MoreInformationDialogTriggerButton } from 'components/shared/more-information';

// Components
import { WidgetCode, WidgetPreview } from 'components/shared/widget';
import { ExpediaPackageWidgetCodeMarkup, ExpediaPackageWidgetPreviewFrame } from 'components/package';

// Hooks
import { useStores } from 'hooks';

// Models
import { Product, Program } from 'models';

interface ExpediaPackageProps {
  program?: Program;
  product?: Product;
  userProgram?: any;
}

const ExpediaPackage = observer(
  // tslint:disable-next-line:no-shadowed-variable
  function ExpediaPackage({ program, product, userProgram }: ExpediaPackageProps): JSX.Element {
    const l10n = useLocalization();
    const { analytics } = useStores();

    const demoLink: string =
      'https://www.expedia.com/marketing/widgets/packagedeals/widget?wtt=5&tp1=CAMREF&tp2=PUBREF&des=LAS&los=7&wws=1&dws=F300F600';

    const demoPreviewWidgetStyles: React.CSSProperties = { width: '300px', height: '600px' };
    const demoCodeMarkupWidgetStyles: string = `width:${'300px'};height:${'600px'};`;

    return (
      <main className="container">
        <UitkLayoutGrid columns={{ large: 2 }} space="twelve">
          <UitkLayoutGridItem colSpan="all">
            <div>
              <section>
                <p>
                  <UitkText inline={true} size={500}>
                    {
                      'Help your visitors find great package deals. When travelers book a flight and hotel together, they can enjoy big savings like 100% off their flight or hotel. Choose a destination that matches your content and let the widget do the rest.'
                    }
                  </UitkText>
                </p>
              </section>
            </div>
          </UitkLayoutGridItem>

          <UitkLayoutGridItem>
            <div>
              <section>
                <form noValidate={true}>
                  <UitkLayoutGrid space="six">
                    <UitkLayoutGridItem>
                      <div>
                        <fieldset>
                          <UitkSpacing margin={{ blockend: 'four' }}>
                            <legend>
                              <UitkHeading tag="h2" size={4}>
                                <LocalizedText message="product.form.tracking-information.pz.heading.partnerize_tracking_information" />
                              </UitkHeading>
                            </legend>
                          </UitkSpacing>

                          <UitkLayoutGrid space="four">
                            <UitkLayoutGridItem>
                              <div>
                                <UitkInformativeInput
                                  id="tracking-pubref"
                                  name="pubref"
                                  label={l10n.formatText('product.form.tracking-information.pz.label.pubref_optional')}
                                  informativeTrigger={<MoreInformationDialogTriggerButton id="pz-pubref" />}
                                  a11yInformativeTrigger={l10n.formatText(
                                    'more-information.dialog.trigger.more_information',
                                  )}
                                  // value={this.state.pubref}
                                  // onChange={this.handlePubrefChange}
                                  // pattern={`[^${this.specialChars.join('')}]*`}
                                  // invalid={this.state.errors?.pubref}
                                />
                                <MoreInformationDialog
                                  id="pz-pubref"
                                  title={l10n.formatText('more-information.dialog.pz-pubref.title.what_pubref')}
                                  description={l10n.formatText(
                                    'more-information.dialog.pz-pubref.description.pubref_optional_reference_include_tracking_link_for_your_reporting_purposes_pubref_tracks_clicks_through_bookings',
                                  )}
                                />
                              </div>
                            </UitkLayoutGridItem>
                          </UitkLayoutGrid>
                        </fieldset>
                      </div>
                    </UitkLayoutGridItem>

                    <UitkLayoutGridItem>
                      <div>
                        <fieldset>
                          <UitkSpacing margin={{ blockend: 'four' }}>
                            <legend>
                              <UitkHeading tag="h2" size={4}>
                                {/* <LocalizedText message="product.bex-search.title.configure" /> */}
                                {'Configuration'}
                              </UitkHeading>
                            </legend>
                          </UitkSpacing>

                          <UitkLayoutGrid space="four">
                            <UitkLayoutGridItem>
                              <div>
                                <UitkSelect id="lengthOfStay" name="los" label="Length of Stay">
                                  <option value="2">{'2 days'}</option>
                                  <option value="3">{'3 days'}</option>
                                  <option value="5">{'5 days'}</option>
                                  <option value="7" selected={true}>
                                    {'7 days'}
                                  </option>
                                  <option value="10">{'10 days'}</option>
                                  <option value="14">{'14 days'}</option>
                                </UitkSelect>
                              </div>
                            </UitkLayoutGridItem>

                            <UitkLayoutGridItem>
                              <div>
                                <UitkSelect id="destinationLabel" name="des" label="Destination">
                                  <option value="CUN">{'Cancun, Quintana Roo, Mexico'}</option>
                                  <option value="PUJ">{'Punta Cana, Dominican Republic'}</option>
                                  <option value="NYC">{'New York, NY'}</option>
                                  <option value="MIA">{'Miami, FL'}</option>
                                  <option value="ORL">{'Orlando, FL'}</option>
                                  <option value="MBJ">{'Montego Bay, Jamaica'}</option>
                                  <option value="LAS" selected={true}>
                                    {'Las Vegas, NV'}
                                  </option>
                                  <option value="SJU">{'San Juan, Puerto Rico'}</option>
                                  <option value="PVR">{'Puerto Vallarta, Jalisco, Mexico'}</option>
                                  <option value="HNL">{'Honolulu, HI'}</option>
                                  <option value="SJD">{'San Jose del Cabo, Baja California Sur, Mexico'}</option>
                                  <option value="PAR">{'Paris, France'}</option>
                                  <option value="AUA">{'Oranjestad, Aruba'}</option>
                                  <option value="OGG">{'Kahului, HI'}</option>
                                  <option value="MCO">{'Orlando, FL'}</option>
                                  <option value="MSY">{'New Orleans, LA'}</option>
                                  <option value="LON">{'London, England, UK'}</option>
                                  <option value="NAS">{'Nassau, Bahamas'}</option>
                                  <option value="SAN">{'San Diego, CA'}</option>
                                  <option value="CHI">{'Chicago, IL'}</option>
                                  <option value="ROM">{'Rome, Italy'}</option>
                                  <option value="QSF">{'San Francisco, CA'}</option>
                                  <option value="LAX">{'Los Angeles, CA (LAX-Los Angeles Intl.)'}</option>
                                  <option value="QLA">{'Los Angeles, CA (QLA-All Airports)'}</option>
                                  <option value="FLL">{'Fort Lauderdale, FL'}</option>
                                  <option value="PHX">{'Phoenix, AZ'}</option>
                                </UitkSelect>
                              </div>
                            </UitkLayoutGridItem>

                            <UitkLayoutGridItem>
                              <div>
                                <fieldset>
                                  <UitkSpacing margin={{ blockend: 'two' }}>
                                    <legend className="is-visually-hidden">
                                      <UitkHeading tag="h3" size={6}>
                                        {/* <LocalizedText message="product.bex-search.configure.form.title.height" /> */}
                                        {'Width'}
                                      </UitkHeading>
                                    </legend>
                                  </UitkSpacing>
                                  <UitkLayoutGrid columns={2} space="three">
                                    <UitkLayoutGridItem>
                                      <div>
                                        <UitkSelect
                                          id="width-sizing"
                                          name="dws-wt"
                                          label={'Measurement unit'}
                                          // value={widthSizing}
                                          // onChange={this.handleWidthSizingSelectChange}
                                        >
                                          <option value={'F'}>{'Fixed (pixels'}</option>
                                          <option value={'R'}>{'Relative (%)'}</option>
                                        </UitkSelect>
                                      </div>
                                    </UitkLayoutGridItem>

                                    <UitkLayoutGridItem>
                                      <div>
                                        <UitkNumberInput
                                          id="width-size"
                                          name="dws-wv"
                                          label={'Width'}
                                          // value={widthSize}
                                          // onChange={this.handleWidthSizeInputChange}
                                          required={true}
                                          // min={widthSizeMin}
                                          // max={widthSizeMax}
                                          // step="any"
                                          // step={widthSizeStep}
                                          // invalid={errors.widthSize}
                                        />
                                      </div>
                                    </UitkLayoutGridItem>
                                  </UitkLayoutGrid>
                                </fieldset>
                              </div>
                            </UitkLayoutGridItem>

                            <UitkLayoutGridItem>
                              <div>
                                <fieldset>
                                  <UitkSpacing margin={{ blockend: 'two' }}>
                                    <legend className="is-visually-hidden">
                                      <UitkHeading tag="h3" size={6}>
                                        {/* <LocalizedText message="product.bex-search.configure.form.title.height" /> */}
                                        {'Height'}
                                      </UitkHeading>
                                    </legend>
                                  </UitkSpacing>
                                  <UitkLayoutGrid columns={2} space="three">
                                    <UitkLayoutGridItem>
                                      <div>
                                        <UitkSelect
                                          id="height-sizing"
                                          name="dws-ht"
                                          label={'Measurement unit'}
                                          // value={heightSizing}
                                          // onChange={this.handleHeightSizingSelectChange}
                                        >
                                          <option value={'F'}>{'Fixed (pixels'}</option>
                                          <option value={'R'}>{'Relative (%)'}</option>
                                        </UitkSelect>
                                      </div>
                                    </UitkLayoutGridItem>

                                    <UitkLayoutGridItem>
                                      <div>
                                        <UitkNumberInput
                                          id="height-size"
                                          name="dws-hv"
                                          label={'Height'}
                                          // value={heightSize}
                                          // onChange={this.handleHeightSizeInputChange}
                                          required={true}
                                          // min={heightSizeMin}
                                          // max={heightSizeMax}
                                          // step="any"
                                          // step={heightSizeStep}
                                          // invalid={errors.heightSize}
                                        />
                                      </div>
                                    </UitkLayoutGridItem>
                                  </UitkLayoutGrid>
                                </fieldset>
                              </div>
                            </UitkLayoutGridItem>

                            <UitkLayoutGridItem>
                              <div>
                                <fieldset>
                                  <UitkSpacing>
                                    <legend>
                                      <UitkHeading tag="h3" size={6}>
                                        {'How would you like the link to open?'}
                                      </UitkHeading>
                                    </legend>
                                  </UitkSpacing>
                                  <UitkRadioGroup group="wws">
                                    <UitkLayoutFlex direction="row" space="four">
                                      <UitkLayoutFlexItem>
                                        <div>
                                          <UitkRadio
                                            id="open-link-new-window"
                                            value={'2'}
                                            // checked={openLink === OpenLink.NEW_WINDOW}
                                          >
                                            {'New window'}
                                          </UitkRadio>
                                        </div>
                                      </UitkLayoutFlexItem>
                                      <UitkLayoutFlexItem>
                                        <div>
                                          <UitkRadio
                                            id="open-link-same-window"
                                            value={'1'}
                                            // checked={openLink === OpenLink.SAME_WINDOW}
                                          >
                                            {'Same window'}
                                          </UitkRadio>
                                        </div>
                                      </UitkLayoutFlexItem>
                                    </UitkLayoutFlex>
                                  </UitkRadioGroup>
                                </fieldset>
                              </div>
                            </UitkLayoutGridItem>
                          </UitkLayoutGrid>
                        </fieldset>
                      </div>
                    </UitkLayoutGridItem>

                    <UitkLayoutGridItem>
                      <div>
                        <fieldset>
                          <UitkSpacing>
                            <legend className="is-visually-hidden">
                              <UitkHeading tag="h3" size={6}>
                                {/* <LocalizedText message="product.bex-search.configure.form.title.actions" /> */}
                                {'Actions'}
                              </UitkHeading>
                            </legend>
                          </UitkSpacing>

                          <UitkLayoutFlex direction="row" space="four">
                            <UitkLayoutFlexItem>
                              <div>
                                <UitkPrimaryButton type="submit">{'Generate Code'}</UitkPrimaryButton>
                              </div>
                            </UitkLayoutFlexItem>

                            <UitkLayoutFlexItem>
                              <div>
                                <UitkSecondaryButton type="reset">{'Reset'}</UitkSecondaryButton>
                              </div>
                            </UitkLayoutFlexItem>
                          </UitkLayoutFlex>
                        </fieldset>
                      </div>
                    </UitkLayoutGridItem>
                  </UitkLayoutGrid>
                </form>
              </section>
            </div>
          </UitkLayoutGridItem>

          <UitkLayoutGridItem>
            <div>
              <UitkLayoutGrid space="six">
                <UitkLayoutGridItem>
                  <div>
                    <WidgetPreview>
                      <ExpediaPackageWidgetPreviewFrame
                        demoLink={demoLink}
                        demoPreviewWidgetStyles={demoPreviewWidgetStyles}
                      />
                    </WidgetPreview>
                  </div>
                </UitkLayoutGridItem>

                <UitkLayoutGridItem>
                  <div>
                    <WidgetCode product={product}>
                      <ExpediaPackageWidgetCodeMarkup
                        demoLink={demoLink}
                        demoCodeMarkupWidgetStyles={demoCodeMarkupWidgetStyles}
                      />
                    </WidgetCode>
                  </div>
                </UitkLayoutGridItem>
              </UitkLayoutGrid>
            </div>
          </UitkLayoutGridItem>
        </UitkLayoutGrid>
      </main>
    );
  },
);

export { ExpediaPackage };
